'use client';

import { useEffect, useRef, useCallback } from 'react';
import { createBrowserClient } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Generate or retrieve visitor ID
function getVisitorId(): string {
    if (typeof window === 'undefined') return '';

    let visitorId = localStorage.getItem('atende_visitor_id');
    if (!visitorId) {
        visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        localStorage.setItem('atende_visitor_id', visitorId);
    }
    return visitorId;
}

// Parse UTM parameters from URL
function getUTMParams(): Record<string, string | null> {
    if (typeof window === 'undefined') return {};

    const params = new URLSearchParams(window.location.search);
    return {
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        utm_term: params.get('utm_term'),
        utm_content: params.get('utm_content'),
        referrer: document.referrer || null
    };
}

// Detect device type
function getDeviceType(): string {
    if (typeof window === 'undefined') return 'unknown';

    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|mini|windows\sce|palm/i.test(ua)) return 'mobile';
    return 'desktop';
}

// Detect browser
function getBrowser(): string {
    if (typeof window === 'undefined') return 'unknown';

    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    if (ua.includes('Opera')) return 'Opera';
    return 'Other';
}

// Detect OS
function getOS(): string {
    if (typeof window === 'undefined') return 'unknown';

    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Other';
}

interface GeoData {
    country: string;
    countryCode: string;
    city: string;
    region: string;
}

// Fetch geolocation from internal API (avoids Mixed Content issues)
async function fetchGeoLocation(): Promise<GeoData | null> {
    try {
        const res = await fetch('/api/geo');
        if (!res.ok) return null;
        const data = await res.json();
        return {
            country: data.country || '',
            countryCode: data.countryCode || '',
            city: data.city || '',
            region: data.region || ''
        };
    } catch {
        return null;
    }
}

export interface VisitorPresenceState {
    visitorId: string;
    userId?: string;
    currentPage: string;
    deviceType: string;
    browser: string;
    os: string;
    country?: string;
    city?: string;
    utmSource?: string;
    utmCampaign?: string;
    startedAt: string;
    lastSeenAt: string;
}

export function useVisitorTracking() {
    const supabase = createBrowserClient();
    const channelRef = useRef<RealtimeChannel | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const visitorIdRef = useRef<string>('');

    const presenceStateRef = useRef<VisitorPresenceState | null>(null);

    const trackPageView = useCallback(async (page: string) => {
        if (!sessionIdRef.current) return;

        try {
            await (supabase.from('visitor_sessions') as any)
                .update({
                    current_page: page
                })
                .eq('id', sessionIdRef.current);
        } catch (err) {
            console.error('Error tracking page view:', err);
        }
    }, [supabase]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const visitorId = getVisitorId();
        visitorIdRef.current = visitorId;

        const initTracking = async () => {
            const utmParams = getUTMParams();
            const deviceType = getDeviceType();
            const browser = getBrowser();
            const os = getOS();
            const currentPage = window.location.pathname;

            // Fetch geolocation
            const geo = await fetchGeoLocation();

            // Create session in database
            const { data: session, error } = await (supabase.from('visitor_sessions') as any)
                .insert({
                    visitor_id: visitorId,
                    utm_source: utmParams.utm_source,
                    utm_medium: utmParams.utm_medium,
                    utm_campaign: utmParams.utm_campaign,
                    utm_term: utmParams.utm_term,
                    utm_content: utmParams.utm_content,
                    referrer: utmParams.referrer,
                    country: geo?.country,
                    country_code: geo?.countryCode,
                    city: geo?.city,
                    region: geo?.region,
                    landing_page: currentPage,
                    current_page: currentPage,
                    pages_visited: [{ page: currentPage, timestamp: new Date().toISOString() }],
                    device_type: deviceType,
                    browser,
                    os
                })
                .select()
                .single();

            if (error) {
                console.error('Error creating visitor session:', error);
                return;
            }

            sessionIdRef.current = session?.id;

            // Join Realtime Presence channel
            const channel = supabase.channel('site_visitors', {
                config: {
                    presence: { key: visitorId }
                }
            });

            const presenceState: VisitorPresenceState = {
                visitorId,
                currentPage,
                deviceType,
                browser,
                os,
                country: geo?.country,
                city: geo?.city,
                utmSource: utmParams.utm_source || undefined,
                utmCampaign: utmParams.utm_campaign || undefined,
                startedAt: new Date().toISOString(),
                lastSeenAt: new Date().toISOString()
            };
            presenceStateRef.current = presenceState;

            channel
                .on('broadcast', { event: 'proactive_chat' }, (payload) => {
                    // Handle proactive chat from admin
                    if (payload.payload?.targetVisitorId === visitorId) {
                        // Dispatch custom event for SupportChatWidget to handle
                        window.dispatchEvent(new CustomEvent('proactive_chat', {
                            detail: {
                                message: payload.payload.message,
                                agentName: payload.payload.agentName
                            }
                        }));
                    }
                })
                .subscribe(async (status) => {
                    if (status === 'SUBSCRIBED') {
                        await channel.track(presenceState);
                    }
                });

            channelRef.current = channel;
        };

        initTracking();

        // Track page changes
        const handleRouteChange = () => {
            const newPage = window.location.pathname;
            trackPageView(newPage);

            // Update presence state
            if (channelRef.current && presenceStateRef.current) {
                const updatedState = {
                    ...presenceStateRef.current,
                    currentPage: newPage,
                    lastSeenAt: new Date().toISOString()
                };
                presenceStateRef.current = updatedState;
                channelRef.current.track(updatedState);
            }
        };

        // Listen for route changes (works with Next.js App Router)
        window.addEventListener('popstate', handleRouteChange);

        // Heartbeat to update last_seen
        const heartbeat = setInterval(() => {
            if (sessionIdRef.current) {
                (supabase.from('visitor_sessions') as any)
                    .update({ last_seen_at: new Date().toISOString() })
                    .eq('id', sessionIdRef.current)
                    .then(() => { });
            }
            if (channelRef.current && presenceStateRef.current) {
                const updatedState = {
                    ...presenceStateRef.current,
                    currentPage: window.location.pathname,
                    lastSeenAt: new Date().toISOString()
                };
                presenceStateRef.current = updatedState;
                channelRef.current.track(updatedState);
            }
        }, 30000); // Every 30 seconds

        // Cleanup
        return () => {
            window.removeEventListener('popstate', handleRouteChange);
            clearInterval(heartbeat);

            // Mark session as ended
            if (sessionIdRef.current) {
                (supabase.from('visitor_sessions') as any)
                    .update({ ended_at: new Date().toISOString() })
                    .eq('id', sessionIdRef.current)
                    .then(() => { });
            }

            // Leave presence channel
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
            }
        };
    }, [supabase, trackPageView]);

    return {
        visitorId: visitorIdRef.current
    };
}
